import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  Filter,
  Search,
  CheckSquare,
  XSquare,
  Calendar,
  User,
  Tag,
  ExternalLink,
  Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useNavigate } from 'react-router-dom';
import './TaskManagement.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TASK_TYPES = {
  follow_up_call: { label: 'Follow-up Call', icon: '📞', color: '#3b82f6' },
  send_email: { label: 'Send Email', icon: '📧', color: '#8b5cf6' },
  send_whatsapp: { label: 'Send WhatsApp', icon: '💬', color: '#10b981' },
  send_proposal: { label: 'Send Proposal', icon: '📄', color: '#f59e0b' },
  schedule_meeting: { label: 'Schedule Meeting', icon: '📅', color: '#ec4899' },
  document_request: { label: 'Document Request', icon: '📎', color: '#14b8a6' },
  payment_reminder: { label: 'Payment Reminder', icon: '💰', color: '#ef4444' },
  custom: { label: 'Custom', icon: '⚙️', color: '#6b7280' }
};

const PRIORITY_CONFIG = {
  urgent: { label: 'Urgent', color: '#ef4444', bg: '#fee2e2' },
  high: { label: 'High', color: '#f59e0b', bg: '#fef3c7' },
  medium: { label: 'Medium', color: '#3b82f6', bg: '#dbeafe' },
  low: { label: 'Low', color: '#6b7280', bg: '#f3f4f6' }
};

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Clock, color: '#f59e0b' },
  in_progress: { label: 'In Progress', icon: CheckCircle2, color: '#3b82f6' },
  completed: { label: 'Completed', icon: CheckCircle2, color: '#10b981' },
  overdue: { label: 'Overdue', icon: AlertCircle, color: '#ef4444' },
  cancelled: { label: 'Cancelled', icon: XSquare, color: '#6b7280' }
};

const TaskManagement = () => {
  const { getAuthHeader, admin } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    priority: 'all',
    viewType: 'my_tasks', // my_tasks, all_tasks
    taskType: 'all',
    workflowName: 'all'
  });

  // Unique workflow names for filter
  const [workflowNames, setWorkflowNames] = useState([]);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    overdue: 0,
    completed: 0,
    my_tasks: 0
  });

  useEffect(() => {
    fetchTasks();
  }, []);

  useEffect(() => {
    applyFilters();
    calculateStats();
  }, [tasks, filters]);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/admin/tasks`, {
        headers: getAuthHeader()
      });
      setTasks(response.data);
      
      // Extract unique workflow names
      const uniqueWorkflows = [...new Set(
        response.data
          .filter(task => task.trigger_name)
          .map(task => task.trigger_name)
      )].sort();
      setWorkflowNames(uniqueWorkflows);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      alert('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...tasks];

    // View type filter (My Tasks vs All Tasks)
    if (filters.viewType === 'my_tasks') {
      filtered = filtered.filter(task => task.assigned_to === admin?.username);
    }

    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(task => task.status === filters.status);
    }

    // Priority filter
    if (filters.priority !== 'all') {
      filtered = filtered.filter(task => task.priority === filters.priority);
    }

    // Task type filter
    if (filters.taskType !== 'all') {
      filtered = filtered.filter(task => task.task_type === filters.taskType);
    }

    // Workflow name filter
    if (filters.workflowName !== 'all') {
      filtered = filtered.filter(task => task.trigger_name === filters.workflowName);
    }

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(task => 
        task.title?.toLowerCase().includes(searchLower) ||
        task.description?.toLowerCase().includes(searchLower) ||
        task.lead_id?.toLowerCase().includes(searchLower)
      );
    }

    // Sort by due date (overdue first, then by date)
    filtered.sort((a, b) => {
      if (a.status === 'overdue' && b.status !== 'overdue') return -1;
      if (a.status !== 'overdue' && b.status === 'overdue') return 1;
      return new Date(a.due_date) - new Date(b.due_date);
    });

    setFilteredTasks(filtered);
  };

  const calculateStats = () => {
    setStats({
      total: tasks.length,
      pending: tasks.filter(t => t.status === 'pending').length,
      overdue: tasks.filter(t => t.status === 'overdue').length,
      completed: tasks.filter(t => t.status === 'completed').length,
      my_tasks: tasks.filter(t => t.assigned_to === admin?.username).length
    });
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await axios.put(
        `${API}/admin/tasks/${taskId}/complete`,
        {},
        { headers: getAuthHeader() }
      );
      fetchTasks();
      alert('Task marked as completed!');
    } catch (error) {
      console.error('Error completing task:', error);
      alert('Failed to complete task');
    }
  };

  const handleCancelTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to cancel this task?')) return;
    
    try {
      await axios.put(
        `${API}/admin/tasks/${taskId}/cancel`,
        {},
        { headers: getAuthHeader() }
      );
      fetchTasks();
      alert('Task cancelled');
    } catch (error) {
      console.error('Error cancelling task:', error);
      alert('Failed to cancel task');
    }
  };

  const handleViewLead = (leadId) => {
    navigate(`/admin/leads?lead_id=${leadId}&openUpdate=true`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  const getTaskTypeInfo = (type) => {
    return TASK_TYPES[type] || TASK_TYPES.custom;
  };

  const getPriorityConfig = (priority) => {
    return PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;
  };

  const getStatusConfig = (status) => {
    return STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  };

  if (loading) {
    return (
      <div className="task-management">
        <div className="task-header">
          <h1><CheckSquare size={28} /> Task Management</h1>
        </div>
        <div className="text-center py-8">Loading tasks...</div>
      </div>
    );
  }

  return (
    <div className="task-management">
      {/* Header with Stats */}
      <div className="task-header-with-stats">
        <div className="task-header-left">
          <h1>
            <CheckSquare size={28} /> Task Management
          </h1>
          <p className="text-muted-foreground">Manage and track your tasks and workflow actions</p>
        </div>

        {/* Mini Stats Cards */}
        <div className="task-mini-stats">
          <div className="mini-stat-card accent-blue">
            <div className="mini-stat-icon">
              <CheckSquare size={20} />
            </div>
            <div className="mini-stat-info">
              <p className="mini-stat-value">{stats.total}</p>
              <p className="mini-stat-label">Total</p>
            </div>
          </div>

          <div className="mini-stat-card accent-orange">
            <div className="mini-stat-icon">
              <Clock size={20} />
            </div>
            <div className="mini-stat-info">
              <p className="mini-stat-value">{stats.pending}</p>
              <p className="mini-stat-label">Pending</p>
            </div>
          </div>

          <div className="mini-stat-card accent-red">
            <div className="mini-stat-icon">
              <AlertCircle size={20} />
            </div>
            <div className="mini-stat-info">
              <p className="mini-stat-value">{stats.overdue}</p>
              <p className="mini-stat-label">Overdue</p>
            </div>
          </div>

          <div className="mini-stat-card accent-green">
            <div className="mini-stat-icon">
              <CheckCircle2 size={20} />
            </div>
            <div className="mini-stat-info">
              <p className="mini-stat-value">{stats.completed}</p>
              <p className="mini-stat-label">Completed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="filters-card">
        <CardContent className="filters-content">
          <div className="filters-grid">
            {/* Search */}
            <div className="filter-item">
              <Search size={18} style={{ color: '#6b7280' }} />
              <Input
                placeholder="Search tasks..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>

            {/* View Type */}
            <Select
              value={filters.viewType}
              onValueChange={(value) => setFilters({ ...filters, viewType: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="my_tasks">My Tasks ({stats.my_tasks})</SelectItem>
                <SelectItem value="all_tasks">All Tasks ({stats.total})</SelectItem>
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select
              value={filters.status}
              onValueChange={(value) => setFilters({ ...filters, status: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            {/* Priority Filter */}
            <Select
              value={filters.priority}
              onValueChange={(value) => setFilters({ ...filters, priority: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>

            {/* Task Type Filter */}
            <Select
              value={filters.taskType}
              onValueChange={(value) => setFilters({ ...filters, taskType: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {Object.entries(TASK_TYPES).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    {config.icon} {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Workflow Name Filter */}
            <Select
              value={filters.workflowName}
              onValueChange={(value) => setFilters({ ...filters, workflowName: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Triggered Workflow" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Triggered Workflows</SelectItem>
                {workflowNames.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="filter-results">
            Showing {filteredTasks.length} of {tasks.length} tasks
          </div>
        </CardContent>
      </Card>

      {/* Tasks List */}
      <div className="tasks-list">
        {filteredTasks.length === 0 ? (
          <Card>
            <CardContent className="empty-state">
              <CheckSquare size={48} style={{ color: '#d1d5db' }} />
              <p style={{ fontSize: '18px', fontWeight: '500', color: '#6b7280', marginTop: '12px' }}>
                No tasks found
              </p>
              <p style={{ fontSize: '14px', color: '#9ca3af' }}>
                Try adjusting your filters or check back later
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredTasks.map((task) => {
            const taskTypeInfo = getTaskTypeInfo(task.task_type);
            const priorityConfig = getPriorityConfig(task.priority);
            const statusConfig = getStatusConfig(task.status);
            const StatusIcon = statusConfig.icon;

            return (
              <Card key={task.id} className="task-card">
                <CardContent className="task-content">
                  <div className="task-main">
                    <div className="task-icon" style={{ background: taskTypeInfo.color + '20', color: taskTypeInfo.color }}>
                      {taskTypeInfo.icon}
                    </div>
                    
                    <div className="task-info">
                      <div className="task-title-row">
                        <h3 className="task-title">{task.title}</h3>
                        {task.is_automated && (
                          <span className="automated-badge">
                            <Zap size={12} /> Automated
                          </span>
                        )}
                      </div>
                      
                      <p className="task-description">{task.description}</p>
                      
                      <div className="task-meta">
                        <span className="meta-item">
                          <Calendar size={14} />
                          Due: {formatDate(task.due_date)}
                        </span>
                        <span className="meta-item">
                          <User size={14} />
                          {task.assigned_to}
                        </span>
                        {task.lead_id && (
                          <span className="meta-item">
                            <Tag size={14} />
                            Lead: {task.lead_id}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="task-actions">
                    <div className="task-badges">
                      <span 
                        className="priority-badge"
                        style={{ 
                          background: priorityConfig.bg, 
                          color: priorityConfig.color,
                          border: `1px solid ${priorityConfig.color}40`
                        }}
                      >
                        {priorityConfig.label}
                      </span>
                      <span 
                        className="status-badge"
                        style={{ color: statusConfig.color }}
                      >
                        <StatusIcon size={14} />
                        {statusConfig.label}
                      </span>
                    </div>

                    <div className="task-buttons">
                      {task.status !== 'completed' && task.status !== 'cancelled' && (
                        <>
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => handleCompleteTask(task.id)}
                            className="task-action-btn task-complete-btn"
                            title="Mark this task as completed"
                          >
                            <CheckCircle2 size={16} /> Mark Complete
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleCancelTask(task.id)}
                            className="task-action-btn-cancel"
                            title="Cancel this task"
                          >
                            <XSquare size={16} /> Cancel
                          </Button>
                        </>
                      )}
                      {task.lead_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewLead(task.lead_id)}
                          className="task-action-btn"
                        >
                          <ExternalLink size={16} /> View Lead
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedTask(task);
                          setIsDetailModalOpen(true);
                        }}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Task Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="task-detail-modal">
          <DialogHeader>
            <DialogTitle>Task Details</DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <div className="task-detail-content">
              <div className="detail-row">
                <strong>Title:</strong>
                <span>{selectedTask.title}</span>
              </div>
              <div className="detail-row">
                <strong>Description:</strong>
                <span>{selectedTask.description}</span>
              </div>
              <div className="detail-row">
                <strong>Type:</strong>
                <span>{getTaskTypeInfo(selectedTask.task_type).label}</span>
              </div>
              <div className="detail-row">
                <strong>Priority:</strong>
                <span>{getPriorityConfig(selectedTask.priority).label}</span>
              </div>
              <div className="detail-row">
                <strong>Status:</strong>
                <span>{getStatusConfig(selectedTask.status).label}</span>
              </div>
              <div className="detail-row">
                <strong>Assigned To:</strong>
                <span>{selectedTask.assigned_to}</span>
              </div>
              <div className="detail-row">
                <strong>Assigned By:</strong>
                <span>{selectedTask.assigned_by}</span>
              </div>
              <div className="detail-row">
                <strong>Due Date:</strong>
                <span>{formatDate(selectedTask.due_date)}</span>
              </div>
              {selectedTask.lead_id && (
                <div className="detail-row">
                  <strong>Lead ID:</strong>
                  <span>{selectedTask.lead_id}</span>
                </div>
              )}
              {selectedTask.is_automated && (
                <div className="detail-row">
                  <strong>Trigger:</strong>
                  <span>{selectedTask.trigger_name || 'Workflow Automation'}</span>
                </div>
              )}
              <div className="detail-row">
                <strong>Created:</strong>
                <span>{formatDate(selectedTask.created_at)}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDetailModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskManagement;
